import React from 'react';
import { ChevronUp, ChevronDown } from 'lucide-react';
import './Table.css';

/**
 * Table Component
 *
 * @param {array} columns - Array of { key, label, sortable?, width?, align? }
 * @param {array} data - Array of row objects
 * @param {function} onSort - Sort handler (key, direction)
 * @param {string} sortKey - Current sort column key
 * @param {string} sortDirection - 'asc' | 'desc'
 * @param {function} onRowClick - Row click handler
 * @param {boolean} loading - Show loading state
 * @param {string} emptyMessage - Message when no data
 */
const Table = ({
  columns = [],
  data = [],
  onSort,
  sortKey,
  sortDirection,
  onRowClick,
  loading = false,
  emptyMessage = 'No data available',
  className = '',
}) => {
  const handleSort = (column) => {
    if (column.sortable && onSort) {
      const newDirection = sortKey === column.key && sortDirection === 'asc' ? 'desc' : 'asc';
      onSort(column.key, newDirection);
    }
  };

  const tableClassNames = [
    'oz-table-container',
    className
  ].filter(Boolean).join(' ');

  return (
    <div className={tableClassNames}>
      <table className="oz-table">
        <thead className="oz-table__head">
          <tr>
            {columns.map((column) => (
              <th
                key={column.key}
                className={`oz-table__th ${column.sortable ? 'oz-table__th--sortable' : ''}`}
                style={{ width: column.width, textAlign: column.align || 'left' }}
                onClick={() => handleSort(column)}
              >
                <div className="oz-table__th-content">
                  <span>{column.label}</span>
                  {column.sortable && (
                    <span className="oz-table__sort-icon">
                      {sortKey === column.key ? (
                        sortDirection === 'asc' ? (
                          <ChevronUp size={16} />
                        ) : (
                          <ChevronDown size={16} />
                        )
                      ) : (
                        <ChevronDown size={16} className="oz-table__sort-icon--inactive" />
                      )}
                    </span>
                  )}
                </div>
              </th>
            ))}
          </tr>
        </thead>
        <tbody className="oz-table__body">
          {loading ? (
            // Loading skeleton
            [...Array(5)].map((_, i) => (
              <tr key={i} className="oz-table__row">
                {columns.map((column) => (
                  <td key={column.key} className="oz-table__td">
                    <div className="oz-table__skeleton" />
                  </td>
                ))}
              </tr>
            ))
          ) : data.length === 0 ? (
            // Empty state
            <tr>
              <td colSpan={columns.length} className="oz-table__empty">
                {emptyMessage}
              </td>
            </tr>
          ) : (
            // Data rows
            data.map((row, index) => (
              <tr
                key={row.id || index}
                className={`oz-table__row ${onRowClick ? 'oz-table__row--clickable' : ''}`}
                onClick={() => onRowClick && onRowClick(row)}
              >
                {columns.map((column) => (
                  <td
                    key={column.key}
                    className="oz-table__td"
                    style={{ textAlign: column.align || 'left' }}
                  >
                    {column.render ? column.render(row[column.key], row) : row[column.key]}
                  </td>
                ))}
              </tr>
            ))
          )}
        </tbody>
      </table>
    </div>
  );
};

// Pagination Component
export const TablePagination = ({
  currentPage = 1,
  totalPages = 1,
  totalItems = 0,
  pageSize = 10,
  onPageChange,
  onPageSizeChange,
  pageSizeOptions = [10, 25, 50, 100],
}) => {
  const start = (currentPage - 1) * pageSize + 1;
  const end = Math.min(currentPage * pageSize, totalItems);

  return (
    <div className="oz-table-pagination">
      <div className="oz-table-pagination__info">
        Showing {start}-{end} of {totalItems} items
      </div>
      <div className="oz-table-pagination__controls">
        <select
          value={pageSize}
          onChange={(e) => onPageSizeChange && onPageSizeChange(Number(e.target.value))}
          className="oz-table-pagination__select"
        >
          {pageSizeOptions.map((size) => (
            <option key={size} value={size}>
              {size} per page
            </option>
          ))}
        </select>
        <div className="oz-table-pagination__buttons">
          <button
            className="oz-table-pagination__btn"
            onClick={() => onPageChange(currentPage - 1)}
            disabled={currentPage <= 1}
          >
            Previous
          </button>
          <span className="oz-table-pagination__page">
            Page {currentPage} of {totalPages}
          </span>
          <button
            className="oz-table-pagination__btn"
            onClick={() => onPageChange(currentPage + 1)}
            disabled={currentPage >= totalPages}
          >
            Next
          </button>
        </div>
      </div>
    </div>
  );
};

export default Table;
